<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import BuhotextCampaignsAPI from 'dashboard/api/buhotextCampaigns';
import router from '../../../../index';

const POLL_INTERVAL = 5000;

export default {
  components: {
    NextButton,
    PageHeader,
  },
  data() {
    return {
      deviceName: '',
      isCreating: false,
      deviceData: null,
      serverInfo: null,
      isLoadingServer: false,
      isLoadingDevice: false,
      isConnecting: false,
      localDeviceId: null,
      localDeviceName: null,
      localSimId: null,
      localPhoneNumber: null,
      pollTimer: null,
    };
  },
  computed: {
    inboxId() {
      return this.$route.params.inbox_id;
    },
    hasDevice() {
      return !!this.localDeviceId;
    },
    isDeviceConnected() {
      return this.hasDevice && this.isOnline;
    },
    isFullyConfigured() {
      return this.hasDevice && !!this.localSimId;
    },
    isOnline() {
      if (!this.deviceData?.last_ping_datetime) return false;
      const lastPing = new Date(this.deviceData.last_ping_datetime);
      const now = new Date();
      const diffMinutes = (now - lastPing) / 1000 / 60;
      return diffMinutes < 5;
    },
    statusLabel() {
      if (!this.hasDevice) return '';
      return this.isOnline
        ? this.$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.STATUS.ONLINE')
        : this.$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.STATUS.OFFLINE');
    },
    statusClass() {
      return this.isOnline
        ? 'bg-green-50 text-green-500 border border-green-200'
        : 'bg-n-slate-3 text-n-slate-11 border border-n-slate-6';
    },
    simCards() {
      return this.deviceData?.sims || this.deviceData?.sim_subscriptions || [];
    },
    apkDownloadUrl() {
      return this.serverInfo?.apk_download_url || '/downloads/buhotext.apk';
    },
    deviceCode() {
      return this.deviceData?.device_code || '';
    },
  },
  mounted() {
    this.loadServerInfo();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    ...mapActions('inboxes', ['updateInbox']),

    async loadServerInfo() {
      this.isLoadingServer = true;
      try {
        const response = await BuhotextCampaignsAPI.serverInfo();
        this.serverInfo = response.data;
      } catch {
        // Silent fail
      } finally {
        this.isLoadingServer = false;
      }
    },

    async loadDeviceDetails() {
      if (!this.localDeviceId) return;
      this.isLoadingDevice = true;
      try {
        const response = await BuhotextCampaignsAPI.getDevice(
          this.localDeviceId
        );
        this.deviceData = response.data.data || response.data;
      } catch {
        // Device may not exist yet
      } finally {
        this.isLoadingDevice = false;
      }
    },

    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(() => {
        this.loadDeviceDetails();
      }, POLL_INTERVAL);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    async handleCreateDevice() {
      if (!this.deviceName.trim()) return;

      this.isCreating = true;
      try {
        const response = await this.$store.dispatch(
          'buhotextCampaigns/createDevice',
          { name: this.deviceName.trim() }
        );
        this.deviceData = response.data || response;

        const deviceId = this.deviceData.id;
        this.localDeviceId = deviceId;
        this.localDeviceName = this.deviceName.trim();

        await this.updateInbox({
          id: this.inboxId,
          formData: false,
          channel: {
            device_id: deviceId,
            device_name: this.deviceName.trim(),
          },
        });

        await this.loadDeviceDetails();
        this.startPolling();

        useAlert(
          this.$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.ALERTS.CREATED')
        );
      } catch {
        useAlert(
          this.$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.ALERTS.CREATE_ERROR')
        );
      } finally {
        this.isCreating = false;
      }
    },

    async handleSelectSim(sim) {
      this.isConnecting = true;
      try {
        const simId = sim.id || sim.subscription_id;
        const phone = sim.phone_number || '';

        await this.updateInbox({
          id: this.inboxId,
          formData: false,
          channel: {
            sim_subscription_id: simId,
            phone_number: phone,
          },
        });
        this.localSimId = simId;
        this.localPhoneNumber = phone;
        this.stopPolling();
        useAlert(
          this.$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.ALERTS.SIM_SELECTED')
        );
      } catch {
        useAlert(
          this.$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.ALERTS.SIM_ERROR')
        );
      } finally {
        this.isConnecting = false;
      }
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        useAlert(this.$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.ALERTS.COPIED'));
      } catch {
        useAlert(
          this.$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.ALERTS.COPY_FAILED')
        );
      }
    },

    handleDownloadApk() {
      window.open(this.apkDownloadUrl, '_blank');
    },

    goToNextStep() {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: {
          page: 'new',
          inbox_id: this.inboxId,
        },
      });
    },

    skipStep() {
      this.goToNextStep();
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.HEADER_TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.HEADER_DESC')"
    />

    <!-- PASO 1: Crear dispositivo -->
    <div v-if="!hasDevice" class="space-y-6">
      <div class="flex-shrink-0 flex-grow-0">
        <label>
          {{ $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.DEVICE_NAME_LABEL') }}
          <input
            v-model="deviceName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.DEVICE_NAME_PLACEHOLDER')
            "
          />
        </label>
      </div>

      <!-- Descargar APK -->
      <div class="p-4 bg-n-alpha-2 border border-n-weak rounded-lg">
        <div class="flex items-start gap-3">
          <div
            class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0"
          >
            <i class="i-lucide-download text-lg text-green-500" />
          </div>
          <div class="flex-1">
            <p class="text-sm font-medium text-n-slate-12">
              {{
                $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.DOWNLOAD_APK_TITLE')
              }}
            </p>
            <p class="text-xs text-n-slate-11 mt-1">
              {{ $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.DOWNLOAD_APK_DESC') }}
            </p>
            <NextButton
              class="mt-2"
              :label="
                $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.DOWNLOAD_APK_BUTTON')
              "
              icon="i-lucide-download"
              slate
              faded
              sm
              @click="handleDownloadApk"
            />
          </div>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <NextButton
          :label="
            $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.CREATE_DEVICE_BUTTON')
          "
          :is-loading="isCreating"
          :disabled="!deviceName.trim()"
          solid
          blue
          @click="handleCreateDevice"
        />
        <NextButton
          :label="$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.SKIP')"
          faded
          slate
          @click="skipStep"
        />
      </div>
    </div>

    <!-- PASO 2: Esperando conexión del dispositivo -->
    <div v-else-if="!isDeviceConnected" class="space-y-6">
      <div
        class="p-4 bg-n-alpha-2 border border-n-weak rounded-lg flex items-center gap-3"
      >
        <div
          class="w-10 h-10 rounded-full bg-n-purple-3 flex items-center justify-center flex-shrink-0"
        >
          <i class="i-lucide-smartphone text-lg text-n-purple-11" />
        </div>
        <div class="flex-1">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ localDeviceName }}
          </h4>
          <span
            :class="statusClass"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mt-1"
          >
            <span class="w-1.5 h-1.5 rounded-full bg-current" />
            {{ statusLabel }}
          </span>
        </div>
      </div>

      <!-- Código de dispositivo -->
      <div
        v-if="deviceCode"
        class="p-4 bg-n-alpha-2 border border-n-weak rounded-lg space-y-3"
      >
        <p class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.APP_CREDENTIALS') }}
        </p>
        <div class="space-y-1">
          <label class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.DEVICE_CODE') }}
          </label>
          <div class="flex gap-1">
            <input
              :value="deviceCode"
              readonly
              class="flex-1 px-2 py-1.5 border border-n-weak rounded text-xs bg-n-slate-2 text-n-slate-12 font-mono"
            />
            <button
              class="px-2 py-1.5 rounded border border-n-weak bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-11 hover:text-n-slate-12 transition-colors"
              type="button"
              @click="copyToClipboard(deviceCode)"
            >
              <i class="i-lucide-copy text-xs" />
            </button>
          </div>
        </div>
      </div>

      <div
        class="p-4 bg-n-blue-3 border border-n-blue-6 rounded-lg flex items-start gap-2"
      >
        <i class="i-lucide-info text-n-blue-11 mt-0.5 flex-shrink-0" />
        <div class="text-sm text-n-blue-11">
          <p class="font-medium">
            {{
              $t(
                'INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.WAITING_CONNECTION_TITLE'
              )
            }}
          </p>
          <p class="mt-1">
            {{
              $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.WAITING_CONNECTION_DESC')
            }}
          </p>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <NextButton
          :label="
            $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.DOWNLOAD_APK_BUTTON')
          "
          icon="i-lucide-download"
          slate
          faded
          @click="handleDownloadApk"
        />
        <NextButton
          :label="$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.SKIP')"
          faded
          slate
          @click="skipStep"
        />
      </div>
    </div>

    <!-- PASO 3: Dispositivo conectado, seleccionar SIM -->
    <div v-else-if="!isFullyConfigured" class="space-y-6">
      <div
        class="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3"
      >
        <div
          class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0"
        >
          <i class="i-lucide-check text-lg text-green-500" />
        </div>
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <h4 class="text-sm font-semibold text-green-400">
              {{
                $t(
                  'INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.DEVICE_CONNECTED_TITLE'
                )
              }}
            </h4>
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-500 border border-green-200"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-current" />
              {{ $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.STATUS.ONLINE') }}
            </span>
          </div>
          <p class="text-xs text-green-500 mt-0.5">
            {{ localDeviceName }}
          </p>
        </div>
      </div>

      <div v-if="simCards.length > 0" class="space-y-3">
        <p class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.SELECT_SIM_TITLE') }}
        </p>
        <button
          v-for="sim in simCards"
          :key="sim.id || sim.subscription_id"
          class="w-full flex items-center justify-between p-3 rounded-lg border border-n-weak hover:border-n-blue-9 hover:bg-n-blue-2 transition-all text-left"
          :disabled="isConnecting"
          type="button"
          @click="handleSelectSim(sim)"
        >
          <div class="flex items-center gap-3">
            <i class="i-lucide-sim-card text-lg text-n-blue-11" />
            <div>
              <div class="text-sm font-medium text-n-slate-12">
                {{
                  sim.carrier_name ||
                  $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.SIM_CARD')
                }}
              </div>
              <div class="text-xs text-n-slate-11">
                {{
                  sim.phone_number ||
                  $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.NO_NUMBER')
                }}
              </div>
            </div>
          </div>
          <i class="i-lucide-chevron-right text-n-slate-11" />
        </button>
      </div>

      <div v-else class="p-4 bg-n-yellow-2 border border-n-yellow-6 rounded-lg">
        <div class="flex gap-2">
          <i class="i-lucide-alert-triangle text-n-yellow-11 mt-0.5" />
          <div class="text-sm text-n-yellow-11">
            <p class="font-medium">
              {{ $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.NO_SIMS_TITLE') }}
            </p>
            <p class="mt-1">
              {{ $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.NO_SIMS_DESC') }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <NextButton
          :label="$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.SKIP')"
          faded
          slate
          @click="skipStep"
        />
      </div>
    </div>

    <!-- PASO 4: Todo configurado -->
    <div v-else class="space-y-6">
      <div
        class="p-6 bg-green-50 border border-green-200 rounded-lg text-center"
      >
        <div
          class="w-14 h-14 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center"
        >
          <i class="i-lucide-check-circle text-2xl text-green-500" />
        </div>
        <h3 class="text-lg font-semibold text-green-400">
          {{ $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.ALL_SET_TITLE') }}
        </h3>
        <p class="text-sm text-green-500 mt-1">
          {{ $t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.ALL_SET_DESC') }}
        </p>
        <p
          v-if="localPhoneNumber"
          class="text-sm font-medium text-green-400 mt-2"
        >
          {{ localPhoneNumber }}
        </p>
      </div>

      <div class="flex justify-end">
        <NextButton
          :label="$t('INBOX_MGMT.ADD.BUHOTEXT.DEVICE_SETUP.CONTINUE')"
          solid
          blue
          @click="goToNextStep"
        />
      </div>
    </div>
  </div>
</template>
